import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Typography,
  Breadcrumbs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Button,
  Tabs,
  Tab,
  Box,
} from "@mui/material";
import Link from "@mui/material/Link";
import axios from "axios";
import Layout from "../../../layout/Layout";
import { AlertMessage } from "../../../components/AlertMessage";
import { config, menuPermissionsAPI } from "../../../config";

// Defined the full menu hierarchy (updated to include Settings category)
const MENU_HIERARCHY = [
  {
    category: "Dashboard",
    menu_key: "dashboard_cat",
    submenus: [
      { key: "dashboard", title: "Dashboard" },
      { key: "po_wo_dashboard", title: "PO & WO Dashboard" },
    ],
  },
  // {
  //   category: "User Portal",
  //   menu_key: "user_portal",
  //   submenus: [
  //     { key: "user_attendance", title: "Attendance" },
  //     { key: "user_apply_leave", title: "Apply Leave" },
  //     { key: "user_labour_attendance", title: "Labour Attendance" },
  //   ],
  // },
  {
    category: "HR Management",
    menu_key: "hr_management",
    submenus: [
      { key: "employees", title: "Employees" },
      { key: "employee_attendance", title: "Employee Attendance" },
      { key: "payroll_process", title: "Payroll Process" },
      { key: "payslip_report", title: "PaySlip Report" },
      { key: "yearly_bonus", title: "Yearly Bonus" },
      { key: "leave_approval", title: "Leave Approval" },
      { key: "hr_advance_payment_report", title: "Advance Payment Report" },
    ],
  },
  {
    category: "Purchase Order",
    menu_key: "purchase_order",
    submenus: [
      { key: "purchase_order_create", title: "Purchase Order" },
      { key: "purchase_order_list", title: "List Purchase Order" },
      { key: "material_receipt_list", title: "Material Receipt" },
      { key: "po_add_terms", title: "P.O Add Terms & Conditions" },
      { key: "po_list_terms", title: "P.O List Terms & Conditions" },
    ],
  },
  {
    category: "Invoice",
    menu_key: "invoice",
    submenus: [
      { key: "invoice_create", title: "Create Invoice" },
      { key: "invoice_list", title: "Invoice List" },
      { key: "invoice_add_terms", title: "Invoice Add Terms & Conditions" },
      { key: "invoice_list_terms", title: "Invoice List Terms & Conditions" },
    ],
  },
  {
    category: "Masters",
    menu_key: "masters",
    submenus: [
      { key: "clients", title: "Clients" },
      { key: "projects", title: "Projects" },
      { key: "store", title: "Store" },
      { key: "vendor", title: "Vendor" },
      { key: "payment_requests", title: "Payment Request" },
    ],
  },
  {
    category: "Configurations",
    menu_key: "configurations",
    submenus: [
      { key: "configuration", title: "Configuration" },
      { key: "departments", title: "Manage Departments" },
      { key: "positions", title: "Manage Positions" },
      { key: "allowances", title: "Manage Allowances" },
      { key: "holidays", title: "Manage Holidays" },
      { key: "companies", title: "Manage Companies" },
      { key: "labour_wages", title: "Manage Labour Wages" },
    ],
  },
  {
    category: "Settings",
    menu_key: "settings",
    submenus: [
      { key: "labour_approvals", title: "Labour Approvals" },
      { key: "advance_relief_approver", title: "Advance Relief Approver" },
      { key: "role_menu_permissions", title: "Role Menu Permissions" },
    ],
  },
  {
    category: "Accounting",
    menu_key: "accounting",
    submenus: [
      { key: "project_payment_report", title: "Project Payment Report" },
      { key: "employee_payment_report", title: "Employee Payment Report" },
      { key: "advance_payment_report", title: "Advance Payment Report" },
      { key: "weekly_payment_report", title: "Weekly Payment Report" },
    ],
  },
  {
    category: "Labour Attendance",
    menu_key: "labour_attendance",
    submenus: [
      { key: "site_engineer_weekly_sheet", title: "Site Engineer Weekly Sheet" },
    ],
  },
];

function RoleMenuSettings() {
  const childRef = useRef();
  const [activeTab, setActiveTab] = useState(0); // 0 = Configured Roles Summary, 1 = Config Matrix
  const [positionsList, setPositionsList] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState("");
  const [permissions, setPermissions] = useState({});
  const [summaryList, setSummaryList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPositions();
    fetchSummaryList();
  }, []);

  const fetchPositions = () => {
    axios({
      method: "GET",
      url: `${menuPermissionsAPI}/positions`,
      headers: config.headers,
    })
      .then((response) => {
        setPositionsList(response.data.result || []);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const fetchSummaryList = () => {
    axios({
      method: "GET",
      url: `${menuPermissionsAPI}/summary`,
      headers: config.headers,
    })
      .then((response) => {
        setSummaryList(response.data.result || []);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handlePositionChange = (positionId) => {
    setSelectedPosition(positionId);
    if (!positionId) {
      setPermissions({});
      return;
    }

    axios({
      method: "GET",
      url: `${menuPermissionsAPI}/by-position/${positionId}`,
      headers: config.headers,
    })
      .then((response) => {
        const fetchedPerms = response.data.result || [];
        const permsMap = {};
        fetchedPerms.forEach((p) => {
          permsMap[p.submenu_key] = {
            can_view: !!p.can_view,
            can_create: !!p.can_create,
            can_update: !!p.can_update,
          };
        });
        setPermissions(permsMap);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleEditClick = (positionId) => {
    handlePositionChange(positionId);
    setActiveTab(1);
  };

  const handlePermissionChange = (submenuKey, permissionType, val) => {
    setPermissions((prev) => ({
      ...prev,
      [submenuKey]: {
        ...prev[submenuKey],
        [permissionType]: val,
      },
    }));
  };

  const handleCategorySelectAll = (categoryKey, val) => {
    const category = MENU_HIERARCHY.find((c) => c.menu_key === categoryKey);
    if (!category) return;

    setPermissions((prev) => {
      const updated = { ...prev };
      category.submenus.forEach((sub) => {
        updated[sub.key] = {
          can_view: val,
          can_create: val,
          can_update: val,
        };
      });
      return updated;
    });
  };

  const handleSave = () => {
    if (!selectedPosition) {
      childRef.current.triggerAlert(
        JSON.stringify({
          message: "Please select a Position/Role",
          messageType: "error",
        })
      );
      return;
    }

    setLoading(true);

    const permissionDataList = [];
    MENU_HIERARCHY.forEach((cat) => {
      cat.submenus.forEach((sub) => {
        const perm = permissions[sub.key];
        if (perm && (perm.can_view || perm.can_create || perm.can_update)) {
          permissionDataList.push({
            menu_key: cat.menu_key,
            submenu_key: sub.key,
            can_view: perm.can_view ? 1 : 0,
            can_create: perm.can_create ? 1 : 0,
            can_update: perm.can_update ? 1 : 0,
          });
        }
      });
    });

    axios({
      method: "POST",
      url: `${menuPermissionsAPI}/save`,
      headers: config.headers,
      data: JSON.stringify({
        positionId: selectedPosition,
        permissions: permissionDataList,
      }),
    })
      .then((response) => {
        setLoading(false);
        childRef.current.triggerAlert(
          JSON.stringify({
            message: response.data.message || "Permissions saved successfully",
            messageType: "success",
          })
        );
        fetchSummaryList();
        // Option to switch back to list view
        setActiveTab(0);
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
        childRef.current.triggerAlert(
          JSON.stringify({
            message: "Something went wrong while saving",
            messageType: "error",
          })
        );
      });
  };

  // Convert DB menu keys into human readable text for allowed list
  const formatAllowedMenus = (menusStr) => {
    if (!menusStr) return "-";
    return menusStr
      .split(", ")
      .map((key) => {
        const hierarchyMatch = MENU_HIERARCHY.find((cat) => cat.menu_key === key);
        return hierarchyMatch ? hierarchyMatch.category : key;
      })
      .join(", ");
  };

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <AlertMessage ref={childRef} />
        <Grid container spacing={3}>
          <Grid textAlign="center" item className="form-lable" xs={12} lg={6} md={6}>
            <Typography className="page_title" textAlign="left">
              Role Menu Permissions Settings
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Settings</Typography>
              <Typography color="text.primary">Role Permissions</Typography>
            </Breadcrumbs>
          </Grid>
        </Grid>

        <div className="empty-space-20"></div>

        <Box sx={{ borderBottom: 1, borderColor: "divider", margin: "10px 0" }}>
          <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)}>
            <Tab label="Configured Permissions List" />
            <Tab label="Configure Role Permissions Matrix" />
          </Tabs>
        </Box>

        <div className="empty-space-20"></div>

        {activeTab === 0 ? (
          /* LIST PAGE FOR ACTIVE PERMISSIONS */
          <TableContainer component={Paper} elevation={3} sx={{ borderRadius: "10px" }}>
            <Table>
              <TableHead sx={{ backgroundColor: "#0c54a0" }}>
                <TableRow>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold", fontSize: "15px" }}>
                    Role / Position Name
                  </TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold", fontSize: "15px" }}>
                    Allowed Modules
                  </TableCell>
                  <TableCell align="center" sx={{ color: "#fff", fontWeight: "bold", fontSize: "15px", width: "150px" }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryList.length > 0 ? (
                  summaryList.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ fontWeight: "bold", color: "#333" }}>
                        {row.position_name}
                      </TableCell>
                      <TableCell color="text.secondary">
                        {formatAllowedMenus(row.allowed_menus)}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleEditClick(row.id)}
                          sx={{ borderColor: "#0c54a0", color: "#0c54a0" }}
                        >
                          Edit Access
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ color: "text.secondary", py: 3 }}>
                      No permissions configured yet. Switch to "Configure Role Permissions Matrix" to set permissions.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          /* ASSIGN MATRIX TAB */
          <>
            <Grid container spacing={2} margin="15px 0px" padding="10px 15px 0px 0px">
              <Grid item xs={12} lg={4} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="position-select-label">Select Position/Role</InputLabel>
                  <Select
                    labelId="position-select-label"
                    label="Select Position/Role"
                    value={selectedPosition}
                    onChange={(e) => handlePositionChange(e.target.value)}
                  >
                    {positionsList.map((pos) => (
                      <MenuItem key={pos.id} value={pos.id}>
                        {pos.position_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} lg={8} md={8} textAlign="right">
                <Button
                  className="btn-primary"
                  variant="contained"
                  onClick={handleSave}
                  disabled={loading || !selectedPosition}
                >
                  Save Permissions
                </Button>
              </Grid>
            </Grid>

            <div className="empty-space-20"></div>

            {selectedPosition ? (
              <TableContainer component={Paper} elevation={3} sx={{ borderRadius: "10px" }}>
                <Table aria-label="permissions matrix table">
                  <TableHead sx={{ backgroundColor: "#0c54a0" }}>
                    <TableRow>
                      <TableCell sx={{ color: "#fff", fontWeight: "bold", fontSize: "15px" }}>
                        Module / Submenu Name
                      </TableCell>
                      <TableCell align="center" sx={{ color: "#fff", fontWeight: "bold", fontSize: "15px" }}>
                        Select All
                      </TableCell>
                      <TableCell align="center" sx={{ color: "#fff", fontWeight: "bold", fontSize: "15px" }}>
                        View Access
                      </TableCell>
                      <TableCell align="center" sx={{ color: "#fff", fontWeight: "bold", fontSize: "15px" }}>
                        Create Access
                      </TableCell>
                      <TableCell align="center" sx={{ color: "#fff", fontWeight: "bold", fontSize: "15px" }}>
                        Update Access
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {MENU_HIERARCHY.map((cat) => {
                      const isCategoryChecked = cat.submenus.every(
                        (sub) =>
                          permissions[sub.key]?.can_view &&
                          permissions[sub.key]?.can_create &&
                          permissions[sub.key]?.can_update
                      );

                      return (
                        <React.Fragment key={cat.menu_key}>
                          <TableRow sx={{ backgroundColor: "#f1f5f9" }}>
                            <TableCell colSpan={1} sx={{ fontWeight: "bold", color: "#0c54a0", fontSize: "14px" }}>
                              {cat.category}
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={isCategoryChecked}
                                onChange={(e) => handleCategorySelectAll(cat.menu_key, e.target.checked)}
                                sx={{ color: "#0c54a0" }}
                              />
                            </TableCell>
                            <TableCell colSpan={3}></TableCell>
                          </TableRow>

                          {cat.submenus.map((sub) => {
                            const perm = permissions[sub.key] || {
                              can_view: false,
                              can_create: false,
                              can_update: false,
                            };

                            return (
                              <TableRow key={sub.key} hover>
                                <TableCell sx={{ paddingLeft: "40px" }}>{sub.title}</TableCell>
                                <TableCell></TableCell>
                                <TableCell align="center">
                                  <Checkbox
                                    checked={perm.can_view}
                                    onChange={(e) =>
                                      handlePermissionChange(sub.key, "can_view", e.target.checked)
                                    }
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Checkbox
                                    checked={perm.can_create}
                                    onChange={(e) =>
                                      handlePermissionChange(sub.key, "can_create", e.target.checked)
                                    }
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Checkbox
                                    checked={perm.can_update}
                                    onChange={(e) =>
                                      handlePermissionChange(sub.key, "can_update", e.target.checked)
                                    }
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box textAlign="center" py={4} sx={{ border: "1px dashed #ccc", borderRadius: "10px" }}>
                <Typography color="text.secondary">
                  Please select a Position/Role above to configure its permissions matrix.
                </Typography>
              </Box>
            )}
          </>
        )}
      </Grid>
    </Layout>
  );
}

export default RoleMenuSettings;
